import { set } from "lodash";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

import { Logger } from "../utils/Logger";
import { MedLocalizationState, MedLocaleTranslateList } from "../types.local";
import { MedLocales, MedLocalizationFilePath, MedLocalizationSetupConfig } from "../types";

@Injectable({
  providedIn: "root",
})
export class StateService {
  private _state: MedLocalizationState = {
    apiUrl: "",
    locales: {},
    isDebug: false,
    projectName: "",
    activeLocale: "",
    defaultLocale: "",
    localesDictionary: {},
    filesList: [],
    ignoreLocales: [],
    loading$: new BehaviorSubject<boolean>(false),
  };

  public logger = new Logger("Localization");

  get locale(): keyof MedLocales {
    return this._state.activeLocale;
  }

  get localeApp(): string {
    if (this._state.ignoreLocales.includes(this.locale as string)) {
      return this._state.defaultLocale as string;
    }

    return this.locale as string;
  }

  get loading$(): BehaviorSubject<boolean> {
    return this._state.loading$;
  }

  get filesList(): MedLocalizationFilePath[] {
    return this._state.filesList;
  }

  get isIgnoreLocale(): boolean {
    return this._state.ignoreLocales.includes(this.locale as string);
  }

  get apiUrl(): string {
    return this._state.apiUrl;
  }

  get projectName(): string {
    return this._state.projectName;
  }

  get isDebug(): boolean {
    return this._state.isDebug;
  }

  get translateList(): MedLocaleTranslateList {
    const { localesDictionary, activeLocale } = this._state;
    const { [activeLocale]: translates } = localesDictionary;
    return translates || {};
  }

  set locale(value: keyof MedLocales) {
    this._state.activeLocale = value;
  }

  set filesList(value: MedLocalizationFilePath[]) {
    this._state.filesList = value;
  }

  init({ locale, locales, apiUrl, projectName, isDebug, ignoreLocales, defaultLocale }: MedLocalizationSetupConfig): void {
    this._state.activeLocale = locale;
    this._state.defaultLocale = defaultLocale;
    this._state.locales = locales;
    this._state.apiUrl = apiUrl;
    this._state.projectName = projectName;
    this._state.ignoreLocales = ignoreLocales || this._state.ignoreLocales;
    this._state.isDebug = isDebug || this._state.isDebug;

    Object.values(locales).forEach(lang => {
      set(this._state.localesDictionary, lang, {});
    });

    if (isDebug) {
      this.logger.log(this._state);
    }
  }

  isTagLocale(tag: string): boolean {
    return Object.values(this._state.locales).includes(tag as string);
  }

  setLoading(value: boolean) {
    this._state.loading$.next(value);
  }

  setTranslations({ folder, file }: MedLocalizationFilePath, translations: MedLocaleTranslateList): void {
    const { localesDictionary, activeLocale } = this._state;

    if (!localesDictionary[activeLocale][folder]) {
      set(localesDictionary[activeLocale], folder, {});
    }

    set(localesDictionary[activeLocale][folder], file, translations);
  }
}
